import { useState } from "react";
import { useReplanMutations, type ReplanRow } from "../../hooks/usePlay";
import type { Persona } from "../../hooks/usePersona";

interface ReplanCardProps {
  replan: ReplanRow;
  workspaceId: string;
  playId: string;
  persona: Persona | null;
}

export function ReplanCard({
  replan,
  workspaceId,
  playId,
  persona,
}: ReplanCardProps) {
  const m = useReplanMutations(workspaceId, playId);
  const [error, setError] = useState<string | null>(null);
  const canAct = persona === "cxo" || persona === "owner";

  const onApprove = async () => {
    setError(null);
    try {
      await m.approveReplan.mutateAsync(replan.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onReject = async () => {
    setError(null);
    const rationale = window.prompt("Reject rationale?") ?? "";
    if (!rationale) return;
    try {
      await m.rejectReplan.mutateAsync({ replanId: replan.id, rationale });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div
      className="ho-detail ho-replan"
      data-testid={`replan-card-${replan.id}`}
    >
      <div className="ho-eb purple">⟲ REPLAN PROPOSED</div>
      <h2 className="ho-h2">{replan.triggerKind.replace(/_/g, " ")}</h2>
      <div className="ho-rp-s" data-testid="replan-rationale">
        {replan.rationale}
      </div>
      {replan.diff ? (
        <div className="ho-rp-d" data-testid="replan-diff">
          <div className="ho-rp-h">DIFF SUMMARY</div>
          <pre
            style={{
              background: "var(--surface-2)",
              padding: 10,
              borderRadius: 6,
              overflow: "auto",
              fontSize: 12,
            }}
          >
            {JSON.stringify(replan.diff, null, 2)}
          </pre>
        </div>
      ) : null}
      {canAct && replan.state === "awaiting_signoff" ? (
        <div className="ho-actions">
          <button
            type="button"
            className="btn primary lg"
            onClick={onApprove}
            disabled={m.approveReplan.isPending}
            data-testid="replan-approve-btn"
          >
            ✓ Approve replan
          </button>
          <button
            type="button"
            className="btn lg"
            onClick={onReject}
            disabled={m.rejectReplan.isPending}
            data-testid="replan-reject-btn"
          >
            Reject
          </button>
        </div>
      ) : null}
      {error ? (
        <span
          style={{ color: "var(--danger)", fontSize: 12 }}
          data-testid="replan-error"
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}
